import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RefreshCw, Send, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  statusColor, flattenWorkflow,
} from "@/pages/signals/rnc/rncData";
import { RncShell, ChannelBadge } from "@/pages/signals/rnc/RncShared";
import { signalsApi, outreachApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { applyJourneyToRnc } from "@/lib/rncJourney";
import { useAssistantName } from "@/hooks/useAssistantName";
import { useAppStore } from "@/store/useAppStore";
import CustomLeadsPanel from "@/pages/signals/CustomLeadsPanel";
import { SIGNALS_TAB_ABM, SIGNALS_TAB_RNC } from "@/pages/signals/signalsNav";
import { FilterSelect } from "@/components/common/FilterSelect";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** V1: only RNC + ABM List load modules; other tabs stay visible and look active. */
const ACTIVE_SIGNAL_SOURCES = new Set(["rnc", "custom"]);
const COMING_SOON_MESSAGE = "This feature is coming in upcoming versions.";

/** Signals channel tabs — active sources first; others remain as future placeholders. */
const FILTER_TABS = [
  ["rnc", "RNC"],
  ["custom", "ABM List"],
  ["linkedin", "LinkedIn"],
  ["quora", "Quora"],
  ["reddit", "Reddit"],
  ["facebook", "Facebook"],
  ["instagram", "Instagram"],
  ["google_ads", "Google Ads"],
  ["website", "Website"],
];

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "In Progress", label: "In Progress" },
  { value: "Qualified as Inquiry Lead", label: "Qualified as Inquiry Lead" },
  { value: "Qualified as Mature Lead", label: "Qualified as Mature Lead" },
  { value: "Unqualified as Inquiry Lead", label: "Unqualified as Inquiry Lead" },
  { value: "Unqualified as Mature Lead", label: "Unqualified as Mature Lead" },
];

function matchesStatusFilter(status, filter) {
  if (filter === "all") return true;
  if (!status) return false;
  return status === filter;
}

const AVATAR_COLORS = ["#4F46E5", "#16A34A", "#DC2626", "#3B82F6", "#14B8A6", "#F97316", "#0A66C2", "#7C3AED"];

function getBarColor(statusCol) {
  if (statusCol === "green") return "#16A34A";
  if (statusCol === "red") return "#DC2626";
  if (statusCol === "amber") return "#D97706";
  return "#94A3B8";
}

function getStepLabel(rnc, flatSteps, workflowAssigned) {
  if (rnc.currentStep) return rnc.currentStep;
  if (workflowAssigned && flatSteps?.[rnc.stepIndex]?.title) {
    return flatSteps[rnc.stepIndex].title;
  }
  if (!workflowAssigned) return "Pending Workflow Assignment";
  return "N/A";
}

function initialsFromName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "PR";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

/**
 * Map live `projects` fields into the approved RNC card shape.
 * Only real DB fields are used. Missing campaign/status/progress → N/A.
 */
function mapProjectToRncCard(project, index) {
  const projectName = project.project_name || "Untitled project";
  const city = project.city || "";
  const country = project.country || "";
  const location = [city, country].filter(Boolean).join(", ");
  const stage = project.stage || null;
  const synced = project.last_synced_at || project.updated_at || null;

  return {
    id: project.project_id || `rnc-${index}`,
    company: projectName,
    contact: location || "—",
    city,
    country,
    currentStep: stage,
    initials: initialsFromName(projectName),
    color: AVATAR_COLORS[index % AVATAR_COLORS.length],
    channel: "rnc",
    // No campaign/workflow assignment on bnc.projects yet — never fake values.
    campaignId: null,
    workflowId: null,
    status: null,
    progress: null,
    lastActivity: synced || null,
    stepIndex: 0,
  };
}

/**
 * Signals → RNC Dashboard — original card/table design preserved.
 * Only Project Name, City, Country, Stage come from the RNC `projects` table.
 */
export default function RncDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const agentName = useAssistantName();
  const { auth } = useAppStore();
  const role = auth?.server?.role || "member";
  const canReset = role === "owner" || role === "admin";
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const filterChannel = useMemo(() => {
    const tab = searchParams.get("tab");
    if (tab && ACTIVE_SIGNAL_SOURCES.has(tab)) return tab;
    return SIGNALS_TAB_RNC;
  }, [searchParams]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [projectsState, setProjectsState] = useState({ loading: true, rows: [], error: null });
  const [reloadToken, setReloadToken] = useState(0);

  const isRncTab = filterChannel === SIGNALS_TAB_RNC;
  const isCustomTab = filterChannel === SIGNALS_TAB_ABM;

  const handleChannelTabClick = useCallback((key) => {
    if (!ACTIVE_SIGNAL_SOURCES.has(key)) {
      toast.message(COMING_SOON_MESSAGE);
      return;
    }
    setSearchParams({ tab: key }, { replace: true });
  }, [setSearchParams]);

  const loadProjects = useCallback(async () => {
    setProjectsState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await signalsApi.listRmcProjects({ limit: 100, offset: 0 });
      const baseRows = (data.projects || []).map((p, i) => mapProjectToRncCard(p, i));
      const projectIds = baseRows.map((r) => r.id).filter(Boolean);
      let journeys = {};
      if (projectIds.length) {
        try {
          const batch = await outreachApi.listRncJourneys(projectIds);
          journeys = batch?.journeys || {};
        } catch {
          journeys = {};
        }
      }
      const rows = baseRows.map((r) => applyJourneyToRnc(r, journeys[r.id]));
      setProjectsState({ loading: false, rows, error: null });
    } catch (e) {
      setProjectsState({
        loading: false,
        rows: [],
        error: e.message || "Could not load RNC projects",
      });
    }
  }, []);

  useEffect(() => {
    if (!isRncTab) return undefined;
    loadProjects();
    return undefined;
  }, [isRncTab, loadProjects, reloadToken]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projectsState.rows.filter((r) => {
      if (!matchesStatusFilter(r.status, statusFilter)) return false;
      if (campaignFilter !== "all" && r.campaignId !== campaignFilter) return false;
      if (!q) return true;
      return (
        r.company.toLowerCase().includes(q)
        || r.contact.toLowerCase().includes(q)
        || (r.city || "").toLowerCase().includes(q)
        || (r.country || "").toLowerCase().includes(q)
        || (r.currentStep || "").toLowerCase().includes(q)
        || (r.campaignName || "").toLowerCase().includes(q)
        || r.id.toLowerCase().includes(q)
      );
    });
  }, [projectsState.rows, search, statusFilter, campaignFilter]);

  const campaignOptions = useMemo(() => {
    const map = new Map();
    projectsState.rows.forEach((r) => {
      if (r.campaignId && r.campaignName) map.set(r.campaignId, r.campaignName);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [projectsState.rows]);

  const total = projectsState.rows.length;

  async function handleResetConfirm() {
    setResetting(true);
    try {
      const res = await signalsApi.resetWorkflows();
      const d = res.deleted || {};
      const r = res.re_enrollment || {};
      toast.success(
        `Workflows reset: ${d.campaign_enrollments ?? 0} enrollments, ${d.campaign_runs ?? 0} runs, ${d.campaign_events ?? 0} events deleted. ${r.enrolled ?? 0} leads re-enrolled.`
      );
      setResetOpen(false);
      setReloadToken((n) => n + 1);
    } catch (e) {
      toast.error("Reset failed: " + (e.message || "Unknown error"));
    } finally {
      setResetting(false);
    }
  }

  return (
    <RncShell>
      <div className="page-head" data-testid="signals-dashboard">
        <div>
          <h1>Signals</h1>
          <p>
            {agentName} monitors every connected channel for buying signals — surfacing opportunities worth your time so you can engage before competitors do.
          </p>
        </div>
        <div className="page-head-actions">
          {canReset && (
            <button type="button" className="btn" onClick={() => setResetOpen(true)} disabled={resetting} data-testid="signals-reset" style={{ color: "#dc2626" }}>
              {resetting ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />} Reset
            </button>
          )}
          <button type="button" className="btn" onClick={() => navigate("/outreach")} data-testid="rnc-goto-campaigns">
            <Send size={15} /> Campaigns
          </button>
        </div>
      </div>

      <div className="tabs tabs--sources" role="tablist" aria-label="Signal sources">
        {FILTER_TABS.map(([key, label]) => {
          const isSelected = filterChannel === key;
          return (
            <button
              type="button"
              key={key}
              role="tab"
              aria-selected={isSelected}
              title={label}
              className={["tab", isSelected ? "on" : ""].filter(Boolean).join(" ")}
              onClick={() => handleChannelTabClick(key)}
              data-testid={`channel-pill-${key}`}
            >
              <ChannelBadge channelKey={key} size={20} />
              <span>{label}</span>
            </button>
          );
        })}
      </div>

      {isCustomTab && (
        <CustomLeadsPanel refreshToken={reloadToken} />
      )}
      {isRncTab && (
      <div className="space-y-3" data-testid="signals-rnc-panel">
      <div className="filters" style={{ marginBottom: 0 }}>
        <input
          placeholder="Search company or contact…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="rnc-search"
        />
        <FilterSelect
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_OPTIONS}
          data-testid="rnc-status-filter"
          aria-label="Status filter"
        />
        <FilterSelect
          value={campaignFilter}
          onChange={setCampaignFilter}
          options={[
            { value: "all", label: "Campaign: All" },
            ...campaignOptions.map((c) => ({ value: c.id, label: c.name })),
          ]}
          data-testid="rnc-campaign-filter"
          aria-label="Campaign filter"
        />
        <button
          type="button"
          className="btn"
          onClick={() => setReloadToken((n) => n + 1)}
          data-testid="rnc-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="card rnc-table-card" data-testid="rnc-database-projects">
        <p className="rnc-table-scroll-hint" aria-hidden="true">Swipe table to see more columns</p>
        <div className="rnc-table-wrap">
          <table className="rnc-data-table rnc-projects-table">
            <thead>
              <tr>
                <th>RNC ID</th>
                <th>COMPANY</th>
                <th>CAMPAIGN</th>
                <th>CURRENT STEP</th>
                <th>STATUS</th>
                <th>PROGRESS</th>
                <th>LAST ACTIVITY</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {projectsState.loading && (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40, color: "var(--slate-400)" }}>
                    Loading projects…
                  </td>
                </tr>
              )}
              {!projectsState.loading && projectsState.error && (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#b91c1c" }}>
                    {projectsState.error}
                  </td>
                </tr>
              )}
              {!projectsState.loading && !projectsState.error && filtered.map((r) => {
                const sc = r.status ? statusColor(r.status) : "slate";
                const barColor = getBarColor(sc);
                const workflowAssigned = Boolean(r.workflowAssigned);
                const flat = flattenWorkflow(r.signalWorkflow);
                const curStepLabel = getStepLabel(r, flat, workflowAssigned);
                const progressPct = r.progress == null ? null : Math.round(Number(r.progress) * 100);

                return (
                  <tr key={r.id} data-testid={`rnc-row-${r.id}`}>
                    <td className="idcell">
                      <button
                        type="button"
                        className="idcell"
                        style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit" }}
                        onClick={() => navigate(`/signals/rnc/${encodeURIComponent(r.id)}`)}
                      >
                        {r.id}
                      </button>
                    </td>
                    <td>
                      <div className="rowcompany">
                        <div className="mk" style={{ background: r.color }}>{r.initials}</div>
                        <div>
                          <div className="nm">{r.company}</div>
                          <div className="sub">{r.contact}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}>{r.campaignName || "N/A"}</td>
                    <td style={{ fontSize: 13 }}>{curStepLabel}</td>
                    <td>
                      {r.status ? (
                        <span className={`status-pill badge ${sc}`}><span className="dotb" />{r.status}</span>
                      ) : (
                        <span style={{ color: "var(--slate-400)", fontSize: 13 }}>N/A</span>
                      )}
                    </td>
                    <td>
                      {progressPct == null ? (
                        <span style={{ color: "var(--slate-400)", fontSize: 13 }}>N/A</span>
                      ) : (
                        <div className="progress-cell">
                          <div className="bar"><div style={{ width: `${progressPct}%`, background: barColor }} /></div>
                          <span>{progressPct}%</span>
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 13 }}>{formatDateTime(r.lastActivity, "N/A")}</td>
                    <td>
                      <button
                        type="button"
                        className="view-details-btn"
                        onClick={() => navigate(`/signals/rnc/${encodeURIComponent(r.id)}`)}
                        data-testid={`view-rnc-${r.id}`}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!projectsState.loading && !projectsState.error && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40, color: "var(--slate-400)" }}>
                    No RNCs match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p style={{ color: "var(--slate-500)", fontSize: 13.5 }}>
        Showing {filtered.length} of {total} RNCs
      </p>
      </div>
      )}

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset all workflows?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all workflow execution data (enrollments, runs, and events) for all leads. Workflows will restart from the beginning.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetConfirm} disabled={resetting} className="bg-red-600 hover:bg-red-700">
              {resetting && <Loader2 size={12} className="animate-spin mr-1" />}
              Reset workflows
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RncShell>
  );
}
